// ── a guide's picture, its card, and a chapter's folder ─────────────────────
// Every guide is ONE colour (its `hue`, lib/community/chapters.ts). The
// thumbnail is that colour's ground with the guide's first real screenshot
// lifted on it and a netti of the matching hue rising from the corner, drawn
// for that exact ground (/api/brand/netti?hue&ground, never a png frozen on
// another ground). The screenshots inside the guide stand on the same ground,
// so the card and what it opens into read as one thing.

import Link from "next/link";
import type { GuideMeta } from "../../lib/community/types";
import { guideGround, guideGroundHex, guideNettiHue, type Chapter } from "../../lib/community/chapters";
import { shotSrc } from "../guides/GuideShot";

export function GuideThumb({ guide, size = "card" }: { guide: GuideMeta; size?: "card" | "mini" }) {
  const ground = guideGround(guide.hue);
  const netti = `/api/brand/netti?hue=${guideNettiHue(guide.hue)}&ground=${encodeURIComponent(guideGroundHex(guide.hue))}`;
  const hero = guide.hero ? shotSrc(guide.hero) : null;
  return (
    <span className="gthumb" data-size={size} data-hero={hero ? "" : undefined} style={{ ["--gt-ground" as string]: ground, ["--gt-hue" as string]: guide.hue } as React.CSSProperties} aria-hidden>
      {/* eslint-disable-next-line @next/next/no-img-element -- a small, cached, pre-blended png */}
      <img className="gthumb__netti" src={netti} alt="" loading="lazy" decoding="async" draggable={false} />
      {hero && size === "card" && (
        // eslint-disable-next-line @next/next/no-img-element -- the guide's own capture
        <img className="gthumb__shot" src={hero} alt="" loading="lazy" decoding="async" draggable={false} />
      )}
    </span>
  );
}

/** one guide as a card: its picture, its place in the chapter, what it covers */
export function GuideCard({
  guide,
  of,
  onPress,
  enter,
}: {
  guide: GuideMeta;
  /** how many steps the chapter has, to say "step 2 of 5" */
  of?: number;
  /** a press that should not navigate (the card in front of a deck) */
  onPress?: () => void;
  enter?: number;
}) {
  return (
    <Link
      className={enter != null ? "cm-card gd-card cm-enter" : "cm-card gd-card"}
      style={enter != null ? ({ ["--i" as string]: enter } as React.CSSProperties) : undefined}
      href={`/community/guides/${guide.slug}`}
      draggable={false}
      onClick={onPress ? (e) => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; e.preventDefault(); onPress(); } : undefined}
    >
      <GuideThumb guide={guide} />
      <span className="cm-card__top">
        <span className="cm-card__cat">{guide.step ? `step ${guide.step}${of ? ` of ${of}` : ""}` : guide.category}</span>
        <span className="cm-card__ver">{guide.minutes} min</span>
      </span>
      <span className="cm-card__t">{guide.title}</span>
      <span className="cm-card__d">{guide.description}</span>
    </Link>
  );
}

/**
 * a chapter on the guides shelf: its first guide's picture, whole — the real
 * capture on its ground, netti rising — with the next guides' colours as the
 * edges of sheets filed behind it. (It was five mini sheets fanned out; at that
 * size a sheet could hold no capture, so the shelf read as blank paper.)
 */
export function ChapterCard({ chapter, guides, enter }: { chapter: Chapter; guides: GuideMeta[]; enter?: number }) {
  const first = guides[0];
  if (!first) return null;
  const minutes = guides.reduce((n, g) => n + g.minutes, 0);
  const behind = guides.slice(1, 3);
  return (
    <Link
      className={enter != null ? "cm-card gd-card gd-chapter cm-enter" : "cm-card gd-card gd-chapter"}
      style={enter != null ? ({ ["--i" as string]: enter } as React.CSSProperties) : undefined}
      href={`/community/guides/${first.slug}`}
      draggable={false}
    >
      <span className="gd-chapter__stack" aria-hidden>
        {behind.map((g, i) => (
          <span
            key={g.slug}
            className="gd-chapter__edge"
            style={{ ["--k" as string]: behind.length - i, ["--gt-ground" as string]: guideGround(g.hue), ["--gt-hue" as string]: g.hue } as React.CSSProperties}
          />
        )).reverse()}
        <GuideThumb guide={first} />
      </span>
      <span className="cm-card__top">
        <span className="cm-card__cat">a chapter</span>
        <span className="cm-card__ver">{guides.length} guides · {minutes} min</span>
      </span>
      <span className="cm-card__t">{chapter.title}</span>
      <span className="cm-card__d">{chapter.description}</span>
    </Link>
  );
}
